import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { PREP_TOUR_DEFAULT_LABELS, PrepTourDialogContent, PrepTourLabels } from './tour.types';

let dialogSeq = 0;

/**
 * Welcome / completion modal — one component, two contents. Rendered inside
 * the outlet (so it survives fullscreen) with its own backdrop and a CDK
 * focus trap; Escape is handled by the outlet.
 */
@Component({
  selector: 'prep-tour-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'prep-tour-dialog' },
  template: `
    <div class="prep-tour-dialog__backdrop" aria-hidden="true"></div>
    <div class="prep-tour-dialog__panel" [class.prep-tour-dialog__panel--centered]="centered"
      role="dialog" aria-modal="true" [attr.aria-labelledby]="ids.title" [attr.aria-describedby]="ids.body"
      cdkTrapFocus [cdkTrapFocusAutoCapture]="true">
      <button *ngIf="content.closeButton" type="button" mat-icon-button class="prep-tour-dialog__close"
        [attr.aria-label]="labels.close" [attr.title]="labels.close" (click)="secondary.emit()">
        <mat-icon aria-hidden="true">close</mat-icon>
      </button>
      <div class="prep-tour-dialog__icon" aria-hidden="true">
        <mat-icon>{{ content.icon || 'check' }}</mat-icon>
      </div>
      <h2 class="prep-tour-dialog__title" [id]="ids.title">{{ content.title }}</h2>
      <p class="prep-tour-dialog__body" [id]="ids.body">{{ content.body }}</p>
      <div class="prep-tour-dialog__actions">
        <button type="button" mat-flat-button color="primary" class="prep-tour-dialog__primary" (click)="primary.emit()">
          {{ content.primary || labels.start }}
        </button>
        <button *ngIf="content.secondary" type="button" mat-button color="primary" (click)="secondary.emit()">
          {{ content.secondary }}
        </button>
      </div>
      <p *ngIf="content.footnote" class="prep-tour-dialog__footnote">{{ content.footnote }}</p>
    </div>
  `,
  styles: [`
    .prep-tour-dialog { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: auto; }
    .prep-tour-dialog__backdrop { position: absolute; inset: 0; background: rgba(15, 23, 42, .45); }
    .prep-tour-dialog__panel {
      position: relative; width: 430px; max-width: calc(100vw - 32px); box-sizing: border-box;
      border-radius: 12px; background: #fff; color: #1e293b; padding: 24px 26px 22px;
      box-shadow: 0 12px 40px rgba(0,0,0,.35); text-align: left;
    }
    .prep-tour-dialog__panel--centered { text-align: center; }
    .prep-tour-dialog__close { position: absolute !important; top: 6px; right: 6px; }
    .prep-tour-dialog__icon {
      display: inline-flex; width: 48px; height: 48px; align-items: center; justify-content: center;
      border-radius: 12px; background: #e8f0fe; color: #1976d2;
    }
    .prep-tour-dialog__icon .mat-icon { font-size: 30px; width: 30px; height: 30px; }
    .prep-tour-dialog__panel--centered .prep-tour-dialog__icon { width: 56px; height: 56px; }
    .prep-tour-dialog__title { margin: 16px 0 0; font-size: 20px; line-height: 1.3; font-weight: 700; color: #0f172a; }
    .prep-tour-dialog__body { margin: 8px 0 0; font-size: 14px; line-height: 1.6; color: #334155; white-space: pre-line; }
    .prep-tour-dialog__actions { margin-top: 20px; display: flex; gap: 10px; }
    .prep-tour-dialog__panel--centered .prep-tour-dialog__actions { justify-content: center; }
    .prep-tour-dialog__footnote { margin: 14px 0 0; font-size: 12px; color: #64748b; }
    .prep-tour-dialog__panel button:focus-visible { outline: 2px solid #1976d2; outline-offset: 2px; }
  `],
})
export class PrepTourDialogComponent implements AfterViewInit {
  @Input() content!: PrepTourDialogContent;
  /** Completion cards are centred; welcome is left-aligned. */
  @Input() centered = false;
  @Input() reducedMotion = false;
  @Input() labels: PrepTourLabels = PREP_TOUR_DEFAULT_LABELS;
  @Output() primary = new EventEmitter<void>();
  @Output() secondary = new EventEmitter<void>();

  readonly ids = (() => {
    const n = dialogSeq++;
    return { title: `prep-tour-dlg-title-${n}`, body: `prep-tour-dlg-body-${n}` };
  })();

  constructor(private readonly host: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    const btn = this.host.nativeElement.querySelector<HTMLElement>('.prep-tour-dialog__primary');
    if (btn) setTimeout(() => btn.focus({ preventScroll: true }), 0);
  }
}
